/**
 * What a match is called, derived rather than stored.
 *
 * The name used to be a string typed once into Setup and wrong for the rest of
 * the week. "Match 6c · Quota (field)" went on saying Sheep Ranch after the tee
 * sheet moved that match to Bandon; one round was called "Team Combined Net"
 * and carried no number at all. A name that is a copy of three other fields is
 * the copy that goes stale, and the only fix is to stop keeping it.
 *
 * So a match holds a NUMBER — the thing the sheet and the group actually use,
 * "1" or "6a" — and everything else in the name is read off the round: the
 * course it is played on, the nine, and the format. Change the course in Setup
 * and the name follows, because there is nothing left to forget to update.
 *
 * Kept free of the scoring engine on purpose. The link unfurler runs this in a
 * Worker on every shared card, and it has no business bundling the tabulator to
 * print a title.
 */

import type { Match, Round } from './types'

/**
 * A course, named the way it is said out loud.
 *
 * A lookup, not a rule about the string: "Bandon Dunes" shortens to Bandon and
 * "Bandon Trails" to Trails, and no rule about first or last words gets both
 * right. Anything not on the list is left exactly as it is — the app is used at
 * other venues, and a guessed abbreviation on a card is a wrong label nobody
 * can trace.
 */
const SHORT_COURSE: Record<string, string> = {
  'bandon dunes': 'Bandon',
  'pacific dunes': 'Pacific',
  'bandon trails': 'Trails',
  'old macdonald': 'Old Mac',
  'sheep ranch': 'Sheep',
  'the preserve': 'Preserve',
}

export const shortCourse = (name: string | undefined): string =>
  name ? (SHORT_COURSE[name.trim().toLowerCase()] ?? name) : ''

/**
 * The format, in the words the 2025 sheet uses.
 *
 * A bare card is deliberately nameless: it is unflighted stroke play, which is
 * what a scorecard IS rather than a contest anybody entered, and calling it
 * "Net Stroke Play" on the week view would imply money that is not there.
 */
export function formatName(config: Match['config'] | undefined): string {
  if (!config) return ''
  switch (config.kind) {
    case 'individualMatchPlay':
      return 'Singles'
    case 'teamHighLow':
      return 'High / Low'
    case 'teamCombinedNet':
      return 'Combined Net'
    case 'teamFourBall':
      return 'Four-ball'
    case 'quota':
      return config.rankingScope === 'group' ? 'Quota (group)' : 'Quota (field)'
    case 'strokeNet':
      return config.flighted ? 'Championship' : ''
  }
}

/**
 * The number, from the field if it has been set and from the old stored label
 * if it has not.
 *
 * Every match in the live document is called "Match 6a · Quota (field)" and has
 * no number of its own. Reading it back out means the screens are right the
 * moment this ships, rather than after somebody has been into Setup and retyped
 * ten numbers from memory.
 */
export function matchNumber(match: Pick<Match, 'number' | 'label'>): string {
  if (match.number !== undefined && match.number !== '') return match.number.trim()
  return /^match\s+(\S+?)\s*(·|$)/i.exec(match.label ?? '')?.[1] ?? ''
}

/** Where a round is played, short: "Old Mac back", "Bandon". */
export function roundWhere(
  round: Pick<Round, 'nine'> | undefined,
  course: { name: string } | undefined,
): string {
  const short = shortCourse(course?.name)
  if (short === '') return ''
  const nine = round?.nine === 'front' ? ' front' : round?.nine === 'back' ? ' back' : ''
  return `${short}${nine}`
}

/**
 * The name, assembled. Falls back to whatever was stored when there is nothing
 * to build one out of — a document with no course on a round is broken in a way
 * this is not the place to shout about.
 */
export function matchName(
  match: Pick<Match, 'number' | 'label'> & { config?: Match['config'] },
  round: Pick<Round, 'nine'> | undefined,
  course: { name: string } | undefined,
): string {
  const n = matchNumber(match)
  const parts = [n === '' ? '' : `Match ${n}`, roundWhere(round, course), formatName(match.config)]
  const name = parts.filter((p) => p !== '').join(' · ')
  return name === '' ? (match.label ?? '') : name
}

/** The same, for a caller holding the document rather than the pieces. */
export function matchNameIn(
  doc: {
    rounds: readonly Round[]
    courses: readonly { id: string; name: string }[]
  },
  match: Pick<Match, 'number' | 'label' | 'roundId'> & { config?: Match['config'] },
): string {
  const round = doc.rounds.find((r) => r.id === match.roundId)
  return matchName(match, round, doc.courses.find((c) => c.id === round?.courseId))
}

/**
 * What a round is called when nobody has renamed it: the course, and the nine.
 *
 * The document's own convention — "Pacific Dunes — front", and no suffix at all
 * on an eighteen, since "Bandon Dunes" IS the round when you play all of it.
 */
export function roundName(
  round: Pick<Round, 'nine'>,
  course: { name: string } | undefined,
): string {
  if (!course) return ''
  const nine = round.nine === 'front' ? ' — front' : round.nine === 'back' ? ' — back' : ''
  return `${course.name}${nine}`
}

/**
 * The label a round should carry after its course or its nine has changed.
 *
 * A round's label is editable, so it cannot simply be derived: "Bandon Trails —
 * the Championship" is a name somebody chose and meant. But left alone it goes
 * stale the moment the round moves, and a stale one is worse than a plain one —
 * a shared card unfurls with the derived match name AND the stored round label,
 * so one line said both "Bandon" and "Sheep Ranch" after the tee sheet changed.
 *
 * So it follows the course while it still matches what the course would have
 * produced, and the moment somebody types over it, it is theirs.
 */
export function nextRoundLabel(current: string, before: string, after: string): string {
  if (after === '') return current
  return current.trim() === '' || current.trim() === before.trim() ? after : current
}
